import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    dylPolishWelcomeGiftLine();
    dylSafeSyncWelcomeGift();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => {
          requestAnimationFrame(() => {
            this.#updateStickyState();
            dylPolishWelcomeGiftLine();
          });
          dylSafeSyncWelcomeGift();
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/* ==========================================================
   Dara y Lissie · Regalo de bienvenida automático
   ----------------------------------------------------------
   Reglas actuales:
   - Variante regalo: 54138128957779 / SKU REGALO5AROMAS
   - Mínimo: 20 € sin contar el propio regalo
   - Si baja de 20 €, se elimina automáticamente
   ========================================================== */

const DYL_WELCOME_GIFT = {
  variantId: 54138128957779,
  sku: 'REGALO5AROMAS',
  thresholdCents: 2000,
};

function dylCustomerCanReceiveWelcomeGift() {
  return Boolean(window.DYL_WELCOME_GIFT_CUSTOMER?.eligible);
}

let dylWelcomeGiftIsSyncing = false;
let dylWelcomeGiftTimer = null;

function dylIsWelcomeGiftLine(item) {
  return Number(item.variant_id) === Number(DYL_WELCOME_GIFT.variantId) || item.sku === DYL_WELCOME_GIFT.sku;
}

function dylCartTotalWithoutWelcomeGift(cart) {
  return cart.items.reduce((total, item) => {
    if (dylIsWelcomeGiftLine(item)) return total;
    return total + Number(item.final_line_price || 0);
  }, 0);
}

function dylFindWelcomeGiftLine(cart) {
  return cart.items.find((item) => dylIsWelcomeGiftLine(item));
}

async function dylFetchCart() {
  const response = await fetch('/cart.js', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error('No se pudo leer el carrito');

  return response.json();
}

async function dylAddWelcomeGift() {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: Number(DYL_WELCOME_GIFT.variantId),
          quantity: 1,
          properties: {
            _dyl_welcome_gift: 'true',
            _dyl_internal_gift: 'REGALO5AROMAS',
          },
        },
      ],
    }),
  });

  if (!response.ok) throw new Error('No se pudo añadir el regalo');

  return response.json();
}

async function dylRemoveWelcomeGift(lineKey) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: lineKey,
      quantity: 0,
    }),
  });

  if (!response.ok) throw new Error('No se pudo quitar el regalo');

  return response.json();
}


function dylPolishWelcomeGiftLine() {
  const drawer = document.querySelector('#cart-drawer') || document;
  const walker = document.createTreeWalker(drawer, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const text = node.textContent?.trim() || '';

    if (!text.includes('Regalo de bienvenida')) continue;

    const textElement = node.parentElement;
    if (!textElement) continue;

    const line = textElement.closest('.cart-items__table-row');
    if (!line) continue;

    line.classList.add('dy-welcome-gift-line');
    node.textContent = '🎁 Pack de bienvenida · 5 aromas sorpresa';
  }
}


function dylReloadCartDrawer() {
  window.location.reload();
}

async function dylSyncWelcomeGiftNow() {
  if (dylWelcomeGiftIsSyncing) return;

  dylWelcomeGiftIsSyncing = true;

  try {
    const cart = await dylFetchCart();
    const giftLine = dylFindWelcomeGiftLine(cart);
    const subtotalWithoutGift = dylCartTotalWithoutWelcomeGift(cart);
    const reachesThreshold = subtotalWithoutGift >= DYL_WELCOME_GIFT.thresholdCents;
    const customerCanReceiveGift = dylCustomerCanReceiveWelcomeGift();

    if (!customerCanReceiveGift) {
      if (giftLine?.key) {
        await dylRemoveWelcomeGift(giftLine.key);
        dylReloadCartDrawer();
      }

      return;
    }

    if (reachesThreshold && !giftLine) {
      await dylAddWelcomeGift();
      dylReloadCartDrawer();
      return;
    }

    if (!reachesThreshold && giftLine?.key) {
      await dylRemoveWelcomeGift(giftLine.key);
      dylReloadCartDrawer();
    }
  } catch (error) {
    console.warn('[Dara y Lissie] Error sincronizando regalo de bienvenida:', error);
  } finally {
    dylWelcomeGiftIsSyncing = false;
  }
}

function dylSyncWelcomeGift() {
  window.clearTimeout(dylWelcomeGiftTimer);
  dylWelcomeGiftTimer = window.setTimeout(() => {
    dylSyncWelcomeGiftNow();
  }, 350);
}

function dylSafeSyncWelcomeGift() {
  try {
    dylSyncWelcomeGift();
  } catch (error) {
    console.warn('[Dara y Lissie] No se pudo iniciar el regalo de bienvenida:', error);
  }
}

window.dylSyncWelcomeGift = dylSyncWelcomeGift;

document.addEventListener('DOMContentLoaded', () => {
  dylPolishWelcomeGiftLine();
  dylSafeSyncWelcomeGift();
});

